/**
 * @weretrade/sentry - React Integration
 *
 * Provides React error boundary, hooks, and components for Sentry.
 *
 * @example
 * ```tsx
 * // In main.tsx
 * import { initSentry, SentryErrorBoundary } from '@weretrade/sentry/react';
 *
 * initSentry({ appName: 'infrastructureDog' });
 *
 * ReactDOM.createRoot(document.getElementById('root')!).render(
 *   <SentryErrorBoundary>
 *     <App />
 *   </SentryErrorBoundary>
 * );
 * ```
 */

import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo } from 'react';
import { getSentryConfig } from './config';

interface InitSentryOptions {
  /** App name for tagging (e.g., 'infrastructureDog') */
  appName?: string;
  /** App version for release tracking */
  version?: string;
  /** DSN override (otherwise uses config) */
  dsn?: string;
}

/**
 * Initialize Sentry for React apps.
 *
 * Call this once in your app's entry point (main.tsx) before rendering.
 *
 * @example
 * ```tsx
 * initSentry({
 *   appName: 'infrastructureDog',
 *   version: import.meta.env.VITE_APP_VERSION,
 * });
 * ```
 */
export function initSentry(options?: InitSentryOptions): void {
  const config = getSentryConfig('react');
  const dsn = options?.dsn || config.dsn;

  if (!dsn) {
    console.warn('[Sentry] No DSN configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: config.environment,
    release: options?.version,

    // Performance monitoring - lower in production to save quota
    tracesSampleRate: config.tracesSampleRate,

    // Session replay configuration
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% when error occurs

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy settings
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Initial scope with app tag
    initialScope: {
      tags: {
        app: options?.appName || 'unknown-app',
      },
    },

    // Filter noisy/non-actionable errors
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || '';

      // Browser quirks
      if (message.includes('ResizeObserver loop')) return null;

      // Third-party script errors
      if (message.includes('Script error')) return null;

      // Chunk loading failures (usually network issues)
      if (message.includes('ChunkLoadError')) return null;
      if (message.includes('Loading chunk')) return null;

      // Extension interference
      if (message.includes('extension')) return null;

      return event;
    },

    // Don't track in development by default
    enabled: config.environment !== 'development',
  });
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI (optional) */
  fallback?: React.ReactElement;
  /** Show Sentry feedback dialog on error */
  showDialog?: boolean;
}

/**
 * Sentry error boundary with fallback UI.
 *
 * Catches React errors and reports them to Sentry.
 *
 * @example
 * ```tsx
 * <SentryErrorBoundary>
 *   <App />
 * </SentryErrorBoundary>
 *
 * // With custom fallback
 * <SentryErrorBoundary fallback={<CustomErrorPage />}>
 *   <App />
 * </SentryErrorBoundary>
 * ```
 */
export function SentryErrorBoundary({
  children,
  fallback,
  showDialog = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: SentryErrorBoundaryProps): any {
  const defaultFallback = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h2 style={{ marginBottom: '1rem' }}>Something went wrong</h2>
      <p style={{ marginBottom: '1.5rem', color: '#666' }}>
        We've been notified and are working on it.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          cursor: 'pointer',
          border: '1px solid #ccc',
          borderRadius: '4px',
          background: '#fff',
        }}
      >
        Reload Page
      </button>
    </div>
  );

  // Use a render function for the fallback to avoid React 18 type issues
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderFallback = (): any => fallback || defaultFallback;

  return (
    <Sentry.ErrorBoundary
      fallback={renderFallback}
      showDialog={showDialog}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

/**
 * Hook to capture errors and messages manually.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const sentry = useSentryCapture();
 *
 *   const handleError = (error: Error) => {
 *     sentry.captureException(error, { component: 'MyComponent' });
 *   };
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useSentryCapture() {
  return useMemo(
    () => ({
      /**
       * Capture an exception with optional context
       */
      captureException: (error: Error, context?: Record<string, unknown>) => {
        Sentry.captureException(error, { extra: context });
      },

      /**
       * Capture a message (for non-error events)
       */
      captureMessage: (message: string, level?: Sentry.SeverityLevel) => {
        Sentry.captureMessage(message, level);
      },

      /**
       * Set the current user for error attribution
       */
      setUser: (user: { id: string; email?: string; username?: string } | null) => {
        Sentry.setUser(user);
      },

      /**
       * Add a breadcrumb for debugging
       */
      addBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => {
        Sentry.addBreadcrumb(breadcrumb);
      },

      /**
       * Set a tag for filtering
       */
      setTag: (key: string, value: string) => {
        Sentry.setTag(key, value);
      },
    }),
    []
  );
}

/**
 * Hook to sync user context with Sentry.
 *
 * Automatically sets/clears user when auth state changes.
 *
 * @example
 * ```tsx
 * function App() {
 *   const auth = useAuth();
 *   useSentryUser(auth.user);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useSentryUser(
  user?: { id: string; email?: string; username?: string } | null
) {
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    } else {
      Sentry.setUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only react to specific user property changes
  }, [user?.id, user?.email, user?.username]);
}

/**
 * Component to sync user context from auth hook.
 *
 * Alternative to useSentryUser hook for component-based usage.
 *
 * @example
 * ```tsx
 * function App() {
 *   const auth = useAuth();
 *
 *   return (
 *     <>
 *       <SentryUserSync user={auth.user} />
 *       <MainApp />
 *     </>
 *   );
 * }
 * ```
 */
export function SentryUserSync({
  user,
}: {
  user?: { id: string; email?: string; username?: string } | null;
}) {
  useSentryUser(user);
  return null;
}

/**
 * HOC to wrap a component with Sentry error boundary.
 *
 * @example
 * ```tsx
 * const SafeWidget = withSentryBoundary(RiskyWidget, {
 *   fallback: <WidgetError />,
 * });
 * ```
 */
// Note: withSentryBoundary may have type issues with some strict tsconfig setups.
// Use SentryErrorBoundary directly if you encounter issues.
export function withSentryBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options?: { fallback?: React.ReactElement }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  return function WrappedComponent(props: P) {
    return (
      <SentryErrorBoundary fallback={options?.fallback}>
        <Component {...(props as any)} />
      </SentryErrorBoundary>
    );
  };
}

// Re-export Sentry for advanced usage
export { Sentry };
